/**
 * Dexie (IndexedDB) database for offline caching and write outbox.
 *
 * Tables
 * ──────
 * leads        — cached Lead objects, scoped to the logged-in user
 * customers    — cached Customer objects
 * callHistory  — cached CallLog objects per lead
 * outbox       — pending writes to be replayed when online;
 *                FIFO per lead (lead_id + sequence guarantee ordering)
 * syncMeta     — last-sync timestamps per entity type
 */

import Dexie, { type Table } from 'dexie'
import type { Lead, CallLog } from '../types'
import type { Customer } from '../types'

// ── Outbox entry ──────────────────────────────────────────────────────────────

export type OutboxMethod = 'PATCH' | 'POST' | 'PUT' | 'DELETE'

export interface OutboxEntry {
  /** Auto-incremented by Dexie — used for global ordering within a lead. */
  id?: number
  /** Scoping so we never replay another user's writes after account switch. */
  userId: number
  tenantId: number
  /** UUID sent as Idempotency-Key header so the backend ignores duplicates. */
  idempotencyKey: string
  /** lead_id this write belongs to; 0 for non-lead mutations. */
  leadId: number
  /** Monotonic counter per leadId — ensures FIFO replay within a lead. */
  sequence: number
  method: OutboxMethod
  /** Relative API path, e.g. "/calls/42" */
  path: string
  body: unknown
  /** ISO timestamp of when the entry was enqueued. */
  enqueuedAt: string
  /** Number of failed replay attempts. */
  retryCount: number
}

// ── Sync metadata ─────────────────────────────────────────────────────────────

export interface SyncMeta {
  /** e.g. "leads", "customers", "callHistory" */
  entity: string
  lastSyncedAt: string | null
}

// ── Cached entity wrappers ────────────────────────────────────────────────────

export interface CachedLead extends Lead {
  _cachedAt: string   // ISO timestamp — compared against TTL on read
}

export interface CachedCustomer extends Customer {
  _cachedAt: string
}

export interface CachedCallLog extends CallLog {
  _cachedAt: string
}

// ── Database class ────────────────────────────────────────────────────────────

class OperonOfflineDB extends Dexie {
  leads!: Table<CachedLead>
  customers!: Table<CachedCustomer>
  callHistory!: Table<CachedCallLog>
  outbox!: Table<OutboxEntry>
  syncMeta!: Table<SyncMeta>

  constructor() {
    super('OperonCRM')

    this.version(1).stores({
      // Leads: PK = id; secondary indices for filtering
      leads:       'id, assigned_to, status, insurance_due_date, _cachedAt',
      // Customers: PK = id
      customers:   'id, _cachedAt',
      // CallLog: PK = id; lookup by lead
      callHistory: 'id, lead_id, _cachedAt',
      // Outbox: auto-PK; compound index for FIFO-per-lead ordering
      outbox:      '++id, [leadId+sequence], userId, tenantId, enqueuedAt',
      // SyncMeta: keyed by entity name
      syncMeta:    'entity',
    })

    // v2: purge any rows with null primary keys that were stored before the
    // null-id guard was added in cache.ts.  Cache tables are pure read-through
    // and can be safely cleared; data is re-fetched from the API on next access.
    this.version(2).stores({}).upgrade(tx =>
      Promise.all([
        tx.table('leads').clear(),
        tx.table('customers').clear(),
        tx.table('callHistory').clear(),
      ])
    )
  }
}

export const offlineDB = new OperonOfflineDB()

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Return true if a cached row's TTL has not expired. */
export function isFresh(cachedAt: string, ttlMs: number): boolean {
  return Date.now() - new Date(cachedAt).getTime() < ttlMs
}

/**
 * Next sequence number for a given leadId in the outbox.
 * Queries the highest existing sequence and adds 1.
 */
export async function nextOutboxSequence(leadId: number): Promise<number> {
  const last = await offlineDB.outbox
    .where('[leadId+sequence]')
    .between([leadId, Dexie.minKey], [leadId, Dexie.maxKey])
    .last()
  return last ? last.sequence + 1 : 0
}
